import { env } from "cloudflare:workers"
import { createMiddleware, createServerFn } from "@tanstack/react-start"
import { setResponseHeaders } from "@tanstack/react-start/server"

import {
  adminActionSchema,
  adminDeleteSchema,
  adminSectionSchema,
  alertSchema,
  contentQuerySchema,
  idSchema,
  inquirySchema,
  loginSchema,
  newsletterSchema,
  profileSchema,
  propertyEditorSchema,
  propertySearchSchema,
  registerSchema,
  saveSearchSchema,
  slugSchema,
} from "@/lib/schemas"
import {
  applyAdminAction,
  archiveProperty,
  authenticateUser,
  createSavedSearch,
  createUser,
  deleteAdminRecord,
  deleteSavedSearch,
  getAdminSectionData,
  getAdminSummary,
  getAlertPreferences,
  getContent,
  getFavorites,
  getFeaturedProperties,
  getMemberDashboard,
  getMyListings,
  getPropertyBySlug,
  getSavedSearches,
  getSuggestedProperties,
  getUserById,
  getUserInquiries,
  resetMemberDemoData,
  saveAlertPreferences,
  saveProperty,
  searchProperties,
  submitInquiry,
  subscribeNewsletter,
  toggleFavorite,
  updateProfile,
} from "@/lib/db.server"
import {
  acquireDemoMutationLease,
  getDemoResetGeneration,
  releaseDemoMutationLease,
} from "@/lib/demo-reset.server"
import { useAppSession } from "@/lib/session.server"
import type { PropertySearch, User } from "@/lib/types"

const privateHeaders = () => {
  setResponseHeaders(
    new Headers({
      "Cache-Control": "private, no-store",
      Vary: "Cookie, Authorization",
    })
  )
}

const publicHeaders = async () => {
  const generation = await getDemoResetGeneration(env.DB)
  setResponseHeaders(
    new Headers({
      // Shared responses must never survive a reset generation boundary.
      "Cache-Control": "no-store",
      "CDN-Cache-Control": "no-store",
      "X-Demo-Reset-Generation": String(generation),
    })
  )
}

const currentUser = async () => {
  const session = await useAppSession()
  const userId = session.data.userId
  if (!userId) {
    return null
  }
  const generation = await getDemoResetGeneration(env.DB)
  if (session.data.resetGeneration !== generation) {
    await session.clear()
    return null
  }
  return userId ? getUserById(userId) : null
}

const writeMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const lease = await acquireDemoMutationLease(env.DB)
    privateHeaders()
    try {
      return await next({
        context: { resetGeneration: lease.resetGeneration },
      })
    } finally {
      await releaseDemoMutationLease(env.DB, lease)
    }
  }
)

const requireUser = async (): Promise<User> => {
  privateHeaders()
  const user = await currentUser()
  if (!user || user.status !== "active") {
    throw new Error("Sign in to continue.")
  }
  return user
}

const requireAdmin = async () => {
  const user = await requireUser()
  if (user.role !== "admin") {
    throw new Error("Administrator access is required.")
  }
  return user
}

export const getViewerFn = createServerFn({ method: "GET" }).handler(
  async () => {
    privateHeaders()
    return currentUser()
  }
)

export const getHomeDataFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const viewer = await currentUser()
    if (viewer) {
      privateHeaders()
    } else {
      await publicHeaders()
    }
    const [featured, home, faq, testimonials] = await Promise.all([
      getFeaturedProperties(viewer?.id),
      getContent({ kind: "home" }),
      getContent({ kind: "faq" }),
      getContent({ kind: "testimonial" }),
    ])
    return { viewer, featured, home, faq, testimonials }
  }
)

export const searchPropertiesFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => propertySearchSchema.parse(data))
  .handler(async ({ data }) => {
    const viewer = await currentUser()
    if (viewer) {
      privateHeaders()
    } else {
      await publicHeaders()
    }
    return searchProperties(data, viewer?.id)
  })

export const getPropertyDetailFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => slugSchema.parse(data))
  .handler(async ({ data }) => {
    const viewer = await currentUser()
    if (viewer) {
      privateHeaders()
    } else {
      await publicHeaders()
    }
    const property = await getPropertyBySlug(data.slug, viewer?.id)
    if (!property) {
      return null
    }
    const suggested = await getSuggestedProperties(property, viewer?.id)
    return { viewer, property, suggested }
  })

export const loginFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => loginSchema.parse(data))
  .handler(async ({ data, context }) => {
    privateHeaders()
    const user = await authenticateUser(data.email, data.password)
    if (!user) {
      return {
        ok: false as const,
        error: "The email or password is incorrect.",
      }
    }
    const session = await useAppSession(
      data.remember ? 60 * 60 * 24 * 30 : 60 * 60 * 24
    )
    await session.update({
      userId: user.id,
      resetGeneration: context.resetGeneration,
    })
    return { ok: true as const, user }
  })

export const logoutFn = createServerFn({ method: "POST" }).handler(async () => {
  privateHeaders()
  const session = await useAppSession()
  await session.clear()
  return { ok: true as const }
})

export const registerFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => registerSchema.parse(data))
  .handler(async ({ data, context }) => {
    privateHeaders()
    const user = await createUser(data)
    const session = await useAppSession()
    await session.update({
      userId: user.id,
      resetGeneration: context.resetGeneration,
    })
    return { ok: true as const, user }
  })

export const requestPasswordResetFn = createServerFn({ method: "POST" })
  .validator((data: unknown) => loginSchema.pick({ email: true }).parse(data))
  .handler(async () => {
    privateHeaders()
    return {
      ok: true as const,
      message:
        "If this address belongs to an account, a reset link has been queued. Demo accounts keep their published passwords.",
    }
  })

export const toggleFavoriteFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    return toggleFavorite(user.id, data.id)
  })

export const getFavoritesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireUser()
    return { user, properties: await getFavorites(user.id) }
  }
)

export const getSavedSearchesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireUser()
    return { user, searches: await getSavedSearches(user.id) }
  }
)

export const createSavedSearchFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => saveSearchSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    return createSavedSearch(user.id, data.name, data.filters)
  })

export const deleteSavedSearchFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    await deleteSavedSearch(user.id, data.id)
    return { ok: true as const }
  })

export const getAlertPreferencesFn = createServerFn({
  method: "GET",
}).handler(async () => {
  const user = await requireUser()
  return { user, preferences: await getAlertPreferences(user.id) }
})

export const saveAlertPreferencesFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => alertSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    await saveAlertPreferences(user.id, data)
    return { ok: true as const }
  })

export const getAccountFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireUser()
    return { user }
  }
)

export const updateProfileFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => profileSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    const updated = await updateProfile(user.id, data)
    return { ok: true as const, user: updated }
  })

export const getDashboardFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireUser()
    const [summary, listings, inquiries] = await Promise.all([
      getMemberDashboard(user),
      getMyListings(user),
      getUserInquiries(user),
    ])
    return {
      user,
      summary,
      recentListings: listings.slice(0, 3),
      recentInquiries: inquiries.slice(0, 4),
    }
  }
)

export const getMyListingsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireUser()
    return { user, listings: await getMyListings(user) }
  }
)

export const savePropertyFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => propertyEditorSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    return saveProperty(user, data)
  })

export const archivePropertyFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => idSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser()
    await archiveProperty(user, data.id)
    return { ok: true as const }
  })

export const submitInquiryFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => inquirySchema.parse(data))
  .handler(async ({ data }) => {
    privateHeaders()
    const viewer = await currentUser()
    return submitInquiry(data, viewer?.id)
  })

export const getPublicContentFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => contentQuerySchema.parse(data))
  .handler(async ({ data }) => {
    await publicHeaders()
    return getContent(data)
  })

export const subscribeNewsletterFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => newsletterSchema.parse(data))
  .handler(async ({ data }) => {
    privateHeaders()
    await subscribeNewsletter(data.email)
    return { ok: true as const }
  })

export const resetMemberDemoDataFn = createServerFn({
  method: "POST",
})
  .middleware([writeMiddleware])
  .handler(async () => {
    const user = await requireUser()
    await resetMemberDemoData(user.id)
    return { ok: true as const }
  })

export const getAdminSummaryFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const user = await requireAdmin()
    return { user, summary: await getAdminSummary() }
  }
)

export const getAdminSectionFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => adminSectionSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireAdmin()
    return { user, data: await getAdminSectionData(data.section) }
  })

export const adminActionFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => adminActionSchema.parse(data))
  .handler(async ({ data }) => {
    await requireAdmin()
    await applyAdminAction(data)
    return { ok: true as const }
  })

export const adminDeleteFn = createServerFn({ method: "POST" })
  .middleware([writeMiddleware])
  .validator((data: unknown) => adminDeleteSchema.parse(data))
  .handler(async ({ data }) => {
    await requireAdmin()
    await deleteAdminRecord(data.kind, data.id)
    return { ok: true as const }
  })

export const normalizeSearchFromUrl = (
  search: Record<string, unknown>
): PropertySearch => {
  const numberValue = (
    key: string,
    options: { integer?: boolean; min?: number; max?: number } = {}
  ) => {
    const value = search[key]
    const parsed =
      typeof value === "number"
        ? value
        : typeof value === "string" && value.trim() !== ""
          ? Number(value)
          : Number.NaN
    if (
      !Number.isFinite(parsed) ||
      (options.integer && !Number.isInteger(parsed)) ||
      (options.min !== undefined && parsed < options.min) ||
      (options.max !== undefined && parsed > options.max)
    ) {
      return undefined
    }
    return parsed
  }
  const booleanValue = (key: string) =>
    search[key] === true || search[key] === "true" ? true : undefined
  const stringValue = (key: string, maxLength: number) =>
    typeof search[key] === "string" && search[key] !== ""
      ? String(search[key]).trim().slice(0, maxLength)
      : undefined

  const transaction = stringValue("transaction", 4)
  const type = stringValue("type", 20)
  const sort = stringValue("sort", 12)

  return propertySearchSchema.parse({
    transaction:
      transaction === "buy" || transaction === "rent" ? transaction : undefined,
    type:
      type === "all" ||
      type === "residential" ||
      type === "commercial" ||
      type === "land" ||
      type === "investment" ||
      type === "holiday-rental"
        ? type
        : undefined,
    keywords: stringValue("keywords", 120),
    reference: stringValue("reference", 30),
    region: stringValue("region", 80),
    locality: stringValue("locality", 80),
    minPrice: numberValue("minPrice", { min: 0 }),
    maxPrice: numberValue("maxPrice", { min: 0 }),
    bedrooms: numberValue("bedrooms", {
      integer: true,
      min: 0,
      max: 20,
    }),
    bathrooms: numberValue("bathrooms", {
      integer: true,
      min: 0,
      max: 20,
    }),
    minFloorArea: numberValue("minFloorArea", { min: 0 }),
    minLandArea: numberValue("minLandArea", { min: 0 }),
    nearCbd: booleanValue("nearCbd"),
    waterfront: booleanValue("waterfront"),
    sort:
      sort === "newest" || sort === "price-asc" || sort === "price-desc"
        ? sort
        : undefined,
    page: numberValue("page", { integer: true, min: 1, max: 1000 }),
  })
}
